import {AuthResponse, UserRead} from "../schemas";
import {apiPublicFetch, clearAccessToken, setAccessToken} from "./client";
import {fetchOrStoreUser} from "./users";
import config from "../config";

// WorkOS User Management authorization endpoint. We build this URL on the client
// (using the publishable client id) and redirect straight to the provider — so the
// login click never hits the backend. Only the code-exchange (/auth/callback) does,
// and by then warmBackend() has had the whole OAuth round-trip to spin it up.
const WORKOS_AUTHORIZE_URL = "https://api.workos.com/user_management/authorize";
const OAUTH_STATE_KEY = "loa_oauth_state";

/**
 * Fire-and-forget ping to wake a scaled-to-zero Cloud Run backend. `keepalive`
 * lets the request outlive the navigation to the OAuth provider. Never throws.
 */
export function warmBackend(): void {
    try {
        void fetch(`${config.apiBasePath}/health`, {
            method: "GET",
            mode: "no-cors",
            keepalive: true,
            cache: "no-store",
        }).catch(() => { /* ignore — this is best-effort */ });
    } catch { /* ignore */ }
}

function randomState(): string {
    try {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    } catch { /* fall through */ }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function beginOAuth(provider: "GoogleOAuth" | "MicrosoftOAuth"): void {
    // Kick the cold start now — it runs while the user is on the provider screen.
    warmBackend();

    // CSRF: round-trip a random state through the provider and verify it on return.
    const state = randomState();
    try {
        sessionStorage.setItem(OAUTH_STATE_KEY, state);
    } catch { /* private mode — state check will be skipped on return */ }

    // Must exactly match a redirect URI registered in WorkOS (and the backend's
    // REACT_APP_BASE_PATH). Falls back to the current origin + /app for local dev.
    const base = config.appBasePath || `${window.location.origin}/app`;
    const params = new URLSearchParams({
        response_type: "code",
        client_id: config.workosClientId,
        redirect_uri: `${base}/oauth-callback`,
        provider,
        state,
    });
    window.location.href = `${WORKOS_AUTHORIZE_URL}?${params.toString()}`;
}

/**
 * Verify (and consume) the OAuth state returned on the callback. Returns true when
 * safe to proceed. Skips the check gracefully if no state was stored.
 */
export function consumeOAuthState(returnedState: string | null): boolean {
    let saved: string | null = null;
    try {
        saved = sessionStorage.getItem(OAUTH_STATE_KEY);
        sessionStorage.removeItem(OAUTH_STATE_KEY);
    } catch { /* ignore */ }
    if (!saved) return true; // nothing to compare against — don't block login
    return returnedState === saved;
}

export async function login(username: string, password: string): Promise<UserRead> {
    const resp = await apiPublicFetch<AuthResponse>("/auth/login", {
        method: "POST",
        headers: {"Content-Type": "application/x-www-form-urlencoded"},
        body: new URLSearchParams({username, password}),
    });
    setAccessToken(resp.access_token);
    return fetchOrStoreUser(resp.user ?? undefined);
}

export function loginGoogle(): void {
    beginOAuth("GoogleOAuth");
}

export function loginMicrosoft(): void {
    beginOAuth("MicrosoftOAuth");
}

export async function authenticateOAuth(code: string): Promise<AuthResponse> {
    const resp = await apiPublicFetch<AuthResponse>("/auth/callback", {
        params: {code},
    });

    if (resp.email_verification_required) {
        return resp;
    }

    setAccessToken(resp.access_token);
    await fetchOrStoreUser(resp.user!);
    return resp;
}

export async function verifyEmailCode(
    code: string,
    pendingAuthenticationToken: string,
): Promise<UserRead> {
    const resp = await apiPublicFetch<AuthResponse>("/auth/verify_email", {
        method: "POST",
        body: {
            code,
            pending_authentication_token: pendingAuthenticationToken,
        },
    });
    setAccessToken(resp.access_token);
    return fetchOrStoreUser(resp.user!);
}

export async function logout(): Promise<void> {
    clearAccessToken();
    localStorage.removeItem("user");
}
